// Custom hook: useMissionPeriod
// Provides state management and business logic for mission search and updates.
import useSlideOver from 'components/SlideOver/useSlideOver';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import UseContext from './useContext';
import Usefetchdata from './usefetchdata';
import { formatDateToYMD } from '../helper/helper';
import {
  migrateSavedStatusSearch,
  parseStatusSearchParam,
  MISSION_STATUS_FILTER_TYPES,
  normalizeStatusSearch,
} from '../constants/missionStatusFilters';
import {
  migrateSavedContractSearch,
  parseContractSearchParam,
  CONTRACT_FILTER_TYPES,
} from '../constants/missionContractFilters';
import {
  migrateSavedRecruitmentSearch,
  parseRecruitmentSearchParam,
  RECRUITMENT_FILTER_TYPES,
} from '../constants/missionRecruitmentFilters';
import {
  migrateSavedSupplierSearch,
  parseSupplierSearchParam,
} from '../constants/missionSupplierFilters';

const FILTER_STORAGE_VERSION = 'mission_filters_v1';

const HARDCODED_FILTER_DEFAULTS = {
  companySearch: [],
  assignedTempWorkerSearch: [],
  recruitmentSearch: [],
  supplierSearch: [],
  contractSearch: [],
  departments: [],
  missionPeriod: ['done', 'inProgress', 'soon'],
  statusSearch: [],
  employeesSearch: [],
};

const serializeFilters = ({
  companySearch,
  assignedTempWorkerSearch,
  recruitmentSearch,
  supplierSearch,
  contractSearch,
  departments,
  missionPeriod,
  statusSearch,
  employeesSearch,
}) => ({
  companySearch: normalizeSelectedSearchOptions(companySearch),
  assignedTempWorkerSearch: normalizeSelectedSearchOptions(assignedTempWorkerSearch),
  recruitmentSearch: [...recruitmentSearch].filter((type) =>
    RECRUITMENT_FILTER_TYPES.includes(type)
  ),
  supplierSearch: [...(supplierSearch || [])],
  contractSearch: [...contractSearch]
    .filter((type) => CONTRACT_FILTER_TYPES.includes(type))
    .slice(0, 1),
  departments: departments.filter((d) => d.checked).map((d) => d.name).sort(),
  missionPeriod: missionPeriod.filter((p) => p.checked).map((p) => p.type).sort(),
  statusSearch: normalizeStatusSearch(statusSearch),
  employeesSearch: employeesSearch.filter((e) => e.checked).map((e) => e._id).sort(),
});

const getFilterStorageKey = (userId) => `${FILTER_STORAGE_VERSION}_${userId || 'anonymous'}`;

const readSavedFilters = (userId) => {
  try {
    const raw = localStorage.getItem(getFilterStorageKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const DEFAULTS_SNAPSHOT = JSON.stringify(HARDCODED_FILTER_DEFAULTS);

const normalizeSelectedSearchOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .filter((item) => item?.value)
    .map((item) => {
      const name =
        item.name ||
        (typeof item.label === 'string'
          ? item.label.replace(/\s*\([^)]*\)\s*$/, '').trim()
          : '') ||
        String(item.value);

      return {
        value: String(item.value),
        name,
        ...(item.id_bestt ? { id_bestt: item.id_bestt } : {}),
      };
    });

const parseSelectedSearchParam = (param) =>
  param
    ? param.split(',').filter(Boolean).map((value) => ({ value, name: value }))
    : [];

const FILTER_URL_KEYS = [
  'recruitment',
  'contract',
  'company',
  'assignedTempWorker',
  'assigned',
  'sourcing',
  'departments',
  'period',
  'status',
  'supplier',
  'adminid',
  'startDate',
  'endDate',
];

const hasFilterUrlParams = (urlParams) =>
  FILTER_URL_KEYS.some((key) => urlParams[key] !== null && urlParams[key] !== '');

const buildFilterSearchParams = ({
  companySearch,
  assignedTempWorkerSearch,
  recruitmentSearch,
  supplierSearch,
  contractSearch,
  departments,
  missionPeriod,
  statusSearch,
  employeesSearch,
  search,
  missionDateStart,
  missionDateEnd,
  editMission,
  currentPage,
}) => {
  const params = new URLSearchParams();

  if (companySearch.length > 0) {
    params.set('company', companySearch.map((item) => item.value).join(','));
  }

  if (assignedTempWorkerSearch.length > 0) {
    params.set(
      'assignedTempWorker',
      assignedTempWorkerSearch.map((item) => item.value).join(',')
    );
  }

  if (recruitmentSearch.length > 0) {
    params.set('recruitment', recruitmentSearch.join(','));
  }

  if (supplierSearch.length > 0) {
    params.set('supplier', supplierSearch.join(','));
  }

  if (contractSearch.length > 0) {
    params.set('contract', contractSearch[0]);
  }

  const checkedDepts = departments.filter((d) => d.checked).map((d) => d.name);
  if (checkedDepts.length > 0) {
    params.set('departments', checkedDepts.join(','));
  }

  const checkedPeriods = missionPeriod.filter((p) => p.checked).map((p) => p.type);
  if (checkedPeriods.length > 0) {
    params.set('period', checkedPeriods.join(','));
  }

  if (statusSearch.length > 0) {
    params.set('status', statusSearch.join(','));
  }

  const checkedEmployees = employeesSearch.filter((e) => e.checked).map((e) => e._id);
  if (checkedEmployees.length > 0) {
    params.set('adminid', checkedEmployees.join(','));
  }

  if (search) {
    params.set('search', search);
  }

  if (missionDateStart) {
    params.set('startDate', formatDateToYMD(missionDateStart));
  }
  if (missionDateEnd) {
    params.set('endDate', formatDateToYMD(missionDateEnd));
  }

  if (editMission) {
    params.set('mission', editMission);
  }

  if (currentPage > 1) {
    params.set('page', String(currentPage));
  }

  return params;
};

const normalizeSerializedFilters = (serialized = {}) => ({
  companySearch: normalizeSelectedSearchOptions(serialized.companySearch),
  assignedTempWorkerSearch: normalizeSelectedSearchOptions(serialized.assignedTempWorkerSearch),
  recruitmentSearch: migrateSavedRecruitmentSearch(serialized),
  supplierSearch: migrateSavedSupplierSearch(serialized),
  contractSearch: migrateSavedContractSearch(serialized),
  departments: Array.isArray(serialized.departments) ? serialized.departments : [],
  missionPeriod: Array.isArray(serialized.missionPeriod)
    ? serialized.missionPeriod
    : [...HARDCODED_FILTER_DEFAULTS.missionPeriod],
  statusSearch: migrateSavedStatusSearch(serialized),
  employeesSearch: Array.isArray(serialized.employeesSearch) ? serialized.employeesSearch : [],
});

const toSavedSnapshot = (serialized) => {
  const normalized = normalizeSerializedFilters(serialized);
  return JSON.stringify({
    companySearch: [...normalized.companySearch].sort((a, b) =>
      a.value.localeCompare(b.value)
    ),
    assignedTempWorkerSearch: [...normalized.assignedTempWorkerSearch].sort((a, b) =>
      a.value.localeCompare(b.value)
    ),
    recruitmentSearch: [...normalized.recruitmentSearch],
    supplierSearch: [...normalized.supplierSearch],
    contractSearch: [...normalized.contractSearch],
    departments: [...normalized.departments].sort(),
    missionPeriod: [...normalized.missionPeriod].sort(),
    statusSearch: [...normalized.statusSearch],
    employeesSearch: [...normalized.employeesSearch].sort(),
  });
};

const ensureRecruitmentTypes = (current, types) => {
  const validCurrent = (Array.isArray(current) ? current : []).filter((type) =>
    RECRUITMENT_FILTER_TYPES.includes(type)
  );
  const validTypes = types.filter((type) => RECRUITMENT_FILTER_TYPES.includes(type));
  return [...new Set([...validCurrent, ...validTypes])];
};

const INACTIVE_MISSION_STATUSES = ['suspended', 'canceled', 'interrupted', 'deleted'];

const pickDefaultMissionId = (missions) => {
  if (!missions?.length) return null;
  const sorted = [...missions].sort((a, b) => {
    const aUrgent =
      a.is_urgent && !INACTIVE_MISSION_STATUSES.includes(a.status) ? 1 : 0;
    const bUrgent =
      b.is_urgent && !INACTIVE_MISSION_STATUSES.includes(b.status) ? 1 : 0;
    return bUrgent - aUrgent;
  });
  return sorted[0].id_mission_real;
};

export default function useMissionPeriod() {
  // External context and dependencies
  const { objID, setObjID, searchMissionResult, setSearchMissionResult } =
    UseContext();
  const { toggleSlideOver } = useSlideOver();
  const { userData } = Usefetchdata();
  const token = localStorage.getItem('xsrfToken');
  const [searchParams, setSearchParams] = useSearchParams();

  const initialUrlParams = useRef({
    company: searchParams.get('company'),
    assignedTempWorker: searchParams.get('assignedTempWorker'),
    recruitment: searchParams.get('recruitment'),
    contract: searchParams.get('contract'),
    assigned: searchParams.get('assigned'),
    sourcing: searchParams.get('sourcing'),
    departments: searchParams.get('departments'),
    period: searchParams.get('period'),
    status: searchParams.get('status'),
    supplier: searchParams.get('supplier'),
    adminid: searchParams.get('adminid'),
    mission: searchParams.get('mission'),
    search: searchParams.get('search'),
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
    page: searchParams.get('page'),
  });
  const pendingMissionFromUrl = useRef(initialUrlParams.current.mission || null);
  const isSyncingMissionFromApp = useRef(false);
  const isInitialMount = useRef(true);
  const filterSourceRef = useRef(
    hasFilterUrlParams(initialUrlParams.current) ? 'url' : 'default'
  );
  const [filtersReady, setFiltersReady] = useState(() =>
    hasFilterUrlParams(initialUrlParams.current)
  );

  const parseDateParam = (value) => {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  // Local state definitions — initialized from URL params when present
  const [searchMissionResultWaiting, setSearchMissionResultWaiting] =
    useState(false);
  const [companySearch, setCompanySearch] = useState(() =>
    parseSelectedSearchParam(initialUrlParams.current.company)
  );
  const [assignedTempWorkerSearch, setAssignedTempWorkerSearch] = useState(() =>
    parseSelectedSearchParam(initialUrlParams.current.assignedTempWorker)
  );
  const [recruitmentSearch, setRecruitmentSearch] = useState(() =>
    parseRecruitmentSearchParam(initialUrlParams.current.recruitment, {
      assigned: initialUrlParams.current.assigned,
      sourcing: initialUrlParams.current.sourcing,
    })
  );
  const [contractSearch, setContractSearch] = useState(() =>
    parseContractSearchParam(initialUrlParams.current.contract)
  );
  const [statusSearch, setStatusSearch] = useState(() =>
    parseStatusSearchParam(initialUrlParams.current.status)
  );
  const [supplierSearch, setSupplierSearch] = useState(() =>
    parseSupplierSearchParam(initialUrlParams.current.supplier)
  );
  const [isMissionSearchSigned, setIsMissionSearchSigned] = useState(null);
  const [isMissionSearchSignatureSend, setIsMissionSearchSignatureSend] =
    useState(null);
  const [isInfoMissionWaiting, setIsInfoMissionWaiting] = useState(false);
  const [isInfoMissionLoadData, setIsInfoMissionLoadData] = useState(false);
  // editMission is derived from the URL — single source of truth, no race with URL syncing.
  const editMission = searchParams.get('mission') || false;
  const setEditMission = useCallback(
    (value) => {
      isSyncingMissionFromApp.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set('mission', String(value));
          else next.delete('mission');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(initialUrlParams.current.page);
    return !isNaN(p) && p >= 1 ? p : 1;
  });
  const [totalPages, setTotalPages] = useState(0);
  const [limitElement] = useState(10);
  const [countPerPage, setCountPerPage] = useState(10);
  const [countTotal, setCountTotal] = useState(0);
  const [hiddenByFiltersCount, setHiddenByFiltersCount] = useState(0);

  const [search, setSearch] = useState(
    () => initialUrlParams.current.search || ''
  );
  const [missionDateRange, setMissionDateRange] = useState(() => [
    parseDateParam(initialUrlParams.current.startDate),
    parseDateParam(initialUrlParams.current.endDate),
  ]);
  const [missionDateStart, missionDateEnd] = missionDateRange;

  // Department filter options — loaded dynamically from API
  const [departments, setDepartments] = useState([]);

  // Mission period filter (temporal)
  const [missionPeriod, setMissionPeriod] = useState(() => {
    const periodParam = initialUrlParams.current.period;
    const urlPeriods = periodParam ? periodParam.split(',') : null;
    return [
      { type: 'done', name: 'Terminée', checked: urlPeriods ? urlPeriods.includes('done') : true },
      { type: 'inProgress', name: 'En cours', checked: urlPeriods ? urlPeriods.includes('inProgress') : true },
      { type: 'soon', name: 'À venir', checked: urlPeriods ? urlPeriods.includes('soon') : true },
    ];
  });

  // Mission status filter (action/visibility) — multi select via statusSearch
  const hasInitializedFilters = useRef(false);
  const savedDepartmentsRef = useRef([]);
  const savedEmployeesRef = useRef([]);

  // Snapshot of what's persisted in localStorage (JSON string for fast comparison)
  const [savedFiltersSnapshot, setSavedFiltersSnapshot] = useState(
    () => DEFAULTS_SNAPSHOT
  );
  const [hasSavedFilters, setHasSavedFilters] = useState(false);

  const [role, setRole] = useState(500);
  const [selectedMissions, setSelectedMissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesSearch, setEmployeesSearch] = useState([]);

  // Update role state from userData
  useEffect(() => {
    if (userData?.role) {
      setRole(userData.role);
    }
  }, [userData]);

  // Initialize filters once: URL params take priority, otherwise localStorage, then defaults
  useEffect(() => {
    if (hasInitializedFilters.current) return;
    if (!userData?._id) return;

    const missionId = initialUrlParams.current.mission || null;

    const useUrlFilters = hasFilterUrlParams(initialUrlParams.current);

    if (useUrlFilters) {
      filterSourceRef.current = 'url';
      const saved = readSavedFilters(userData._id);
      if (saved) {
        setHasSavedFilters(true);
        setSavedFiltersSnapshot(toSavedSnapshot(saved));
      }
      hasInitializedFilters.current = true;
      setFiltersReady(true);
      return;
    }

    // Direct link to a specific mission: don't apply saved filters so the mission
    // is guaranteed to appear in the list (defaults are broad enough to include it).
    // Saved filters stay in localStorage and remain available via the "load saved" UI.
    if (missionId) {
      filterSourceRef.current = 'default';
      const savedDirect = readSavedFilters(userData._id);
      if (savedDirect) {
        setHasSavedFilters(true);
        setSavedFiltersSnapshot(toSavedSnapshot(savedDirect));
      }
      hasInitializedFilters.current = true;
      setFiltersReady(true);
      return;
    }

    const saved = readSavedFilters(userData._id);
    if (saved) {
      filterSourceRef.current = 'localStorage';
      savedDepartmentsRef.current = saved.departments || [];
      savedEmployeesRef.current = saved.employeesSearch || [];
      setHasSavedFilters(true);
      setSavedFiltersSnapshot(toSavedSnapshot(saved));

      setRecruitmentSearch(migrateSavedRecruitmentSearch(saved));
      setSupplierSearch(migrateSavedSupplierSearch(saved));
      setCompanySearch(normalizeSelectedSearchOptions(saved.companySearch));
      setAssignedTempWorkerSearch(
        normalizeSelectedSearchOptions(saved.assignedTempWorkerSearch)
      );
      setContractSearch(migrateSavedContractSearch(saved));
      setStatusSearch(migrateSavedStatusSearch(saved));

      setMissionPeriod((prev) =>
        prev.map((p) => ({
          ...p,
          checked: (saved.missionPeriod || HARDCODED_FILTER_DEFAULTS.missionPeriod).includes(p.type),
        }))
      );
    } else {
      filterSourceRef.current = 'default';
      setSavedFiltersSnapshot(DEFAULTS_SNAPSHOT);
      setHasSavedFilters(false);
    }

    hasInitializedFilters.current = true;
    setFiltersReady(true);
  }, [userData]);

  // Fetch department missions from the API (apply URL, saved filters, or defaults)
  const getDepartmentMissions = () => {
    ApiDatabase.getDepartmentMissions(
      { token },
      (data) => {
        if (data.departments.length > 0) {
          const source = filterSourceRef.current;
          const urlDepts = initialUrlParams.current.departments
            ? initialUrlParams.current.departments.split(',')
            : [];
          const checkedNames =
            source === 'url'
              ? urlDepts
              : source === 'localStorage'
                ? savedDepartmentsRef.current
                : [];
          const deps = data.departments.map((department) => ({
            name: department,
            checked: checkedNames.includes(department),
          }));
          setDepartments(deps);
        }
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  };

  // Mémoriser les valeurs filtrées pour éviter les re-renders infinis
  const departmentsFiltered = useMemo(
    () => departments.filter((item) => item.checked).map((item) => item.name),
    [departments]
  );
  const missionPeriodFiltered = useMemo(
    () => missionPeriod.filter((item) => item.checked).map((item) => item.type),
    [missionPeriod]
  );
  const employeesSearchFiltered = useMemo(
    () => employeesSearch.filter((item) => item.checked).map((item) => item._id),
    [employeesSearch]
  );

  // Function to perform mission search based on filters and search input
  // Mémorisée avec useCallback pour éviter les re-renders infinis
  const manageMissionSearch = useCallback(() => {
    setSearchMissionResultWaiting(true);
    setIsInfoMissionLoadData(false);
    setIsInfoMissionWaiting(false);

    ApiDatabase.searchMission(
      {
        token,
        search,
        companySearch: companySearch.map((item) => item.value),
        assignedTempWorkerSearch: assignedTempWorkerSearch.map((item) => item.value),
        recruitmentSearch,
        supplierSearch,
        typeContract: contractSearch,
        departments: departmentsFiltered,
        missionStatus: [
          ...missionPeriodFiltered,
          ...statusSearch,
          ...(statusSearch.includes('canceled') ? ['deleted'] : []),
        ],
        employeesSearch: employeesSearchFiltered,
        missionDateStart: missionDateStart ? formatDateToYMD(missionDateStart) : null,
        missionDateEnd: missionDateEnd ? formatDateToYMD(missionDateEnd) : null,
        limit: limitElement,
        page: currentPage,
      },
      (data) => {
        setSearchMissionResult(data.missions);
        setCountTotal(data.count);
        setCountPerPage(data.countPerPage);
        setTotalPages(data.countPages);

        if (data.missions.length === 0) {
          ApiDatabase.searchMission(
            {
              token,
              search,
              companySearch: [],
              assignedTempWorkerSearch: [],
              recruitmentSearch: [],
              supplierSearch: [],
              typeContract: [],
              departments: [],
              missionStatus: [],
              employeesSearch: [],
              missionDateStart: null,
              missionDateEnd: null,
              limit: 1,
              page: 1,
            },
            (probe) => setHiddenByFiltersCount(probe.count || 0),
            () => setHiddenByFiltersCount(0)
          );
        } else {
          setHiddenByFiltersCount(0);
        }

        const defaultMissionId = pickDefaultMissionId(data.missions);
        if (data.missions.length > 0) {
          if (pendingMissionFromUrl.current) {
            const found = data.missions.find(
              (m) => m.id_mission_real === pendingMissionFromUrl.current
            );
            setEditMission(
              found ? pendingMissionFromUrl.current : defaultMissionId
            );
            pendingMissionFromUrl.current = null;
          } else {
            setEditMission(defaultMissionId);
          }
        } else {
          setEditMission(false);
        }
        setSearchMissionResultWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          setSearchMissionResultWaiting(false);
          setSearchMissionResult([]);
          console.error(err);
        }
      }
    );
  }, [
    token,
    search,
    companySearch,
    assignedTempWorkerSearch,
    recruitmentSearch,
    supplierSearch,
    contractSearch,
    departmentsFiltered,
    missionPeriodFiltered,
    statusSearch,
    employeesSearchFiltered,
    missionDateStart,
    missionDateEnd,
    limitElement,
    currentPage,
    setSearchMissionResult,
  ]);

  // Effects to trigger search when filters or pagination change (after init)
  useEffect(() => {
    if (!filtersReady) return;
    manageMissionSearch();
  }, [
    filtersReady,
    companySearch,
    assignedTempWorkerSearch,
    recruitmentSearch,
    supplierSearch,
    contractSearch,
    departments,
    missionPeriod,
    statusSearch,
    employeesSearch,
    missionDateStart,
    missionDateEnd,
    currentPage,
  ]);

  // Debounce search input change to reduce API calls
  useEffect(() => {
    if (!filtersReady) return;
    const timer = setTimeout(() => {
      manageMissionSearch();
    }, 400);
    return () => clearTimeout(timer);
  }, [search, filtersReady]);

  // Reset to first page when any filter or search input changes (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [
    companySearch,
    assignedTempWorkerSearch,
    recruitmentSearch,
    supplierSearch,
    contractSearch,
    departments,
    missionPeriod,
    statusSearch,
    employeesSearch,
    search,
    missionDateStart,
    missionDateEnd,
  ]);

  // Sync filter state to URL params (after init, only when params actually change)
  useEffect(() => {
    if (!filtersReady) return;

    const params = buildFilterSearchParams({
      companySearch,
      assignedTempWorkerSearch,
      recruitmentSearch,
      supplierSearch,
      contractSearch,
      departments,
      missionPeriod,
      statusSearch,
      employeesSearch,
      search,
      missionDateStart,
      missionDateEnd,
      editMission,
      currentPage,
    });

    const nextQuery = params.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery !== currentQuery) {
      setSearchParams(params, { replace: true });
    }
  }, [
    companySearch,
    assignedTempWorkerSearch,
    recruitmentSearch,
    supplierSearch,
    contractSearch,
    departments,
    missionPeriod,
    statusSearch,
    employeesSearch,
    search,
    missionDateStart,
    missionDateEnd,
    editMission,
    currentPage,
    filtersReady,
    searchParams,
  ]);

  // Cmd+K / direct link: honour mission param on the next search response only
  const prevMissionParam = useRef(searchParams.get('mission'));
  useEffect(() => {
    const missionParam = searchParams.get('mission');
    if (missionParam && missionParam !== prevMissionParam.current) {
      if (!isSyncingMissionFromApp.current) {
        pendingMissionFromUrl.current = missionParam;
      }
      isSyncingMissionFromApp.current = false;
    }
    prevMissionParam.current = missionParam;
  }, [searchParams]);

  // Fetch departments after filters are initialized
  useEffect(() => {
    if (!filtersReady) return;
    getDepartmentMissions();
  }, [filtersReady]);

  // Reset related search states when filled missions are excluded from recruitment filter
  useEffect(() => {
    if (recruitmentSearch.length > 0 && !recruitmentSearch.includes('filled')) {
      setIsMissionSearchSigned(false);
      setIsMissionSearchSignatureSend(false);
    }
  }, [recruitmentSearch]);

  // Automatically update recruitment filter based on signature status
  useEffect(() => {
    if (isMissionSearchSigned === true) {
      setRecruitmentSearch((prev) => ensureRecruitmentTypes(prev, ['filled']));
      setIsMissionSearchSignatureSend(true);
    }
  }, [isMissionSearchSigned]);

  useEffect(() => {
    if (isMissionSearchSignatureSend === true) {
      setRecruitmentSearch((prev) => ensureRecruitmentTypes(prev, ['filled']));
    } else if (isMissionSearchSignatureSend === false) {
      setIsMissionSearchSigned(false);
    }
  }, [isMissionSearchSignatureSend]);

  // Opens the company info slide-over panel
  const openCompanyInfo = (id) => {
    setObjID(id);
    toggleSlideOver('get_company');
  };

  // Set default search parameters based on user role (only when no URL / saved filters)
  useEffect(() => {
    if (!filtersReady) return;
    if (filterSourceRef.current !== 'default') return;
    if (role === 525) {
      setRecruitmentSearch(['filled']);
      setIsMissionSearchSignatureSend(false);
    }
  }, [role]);

  const updateContractSearch = (selectedTypes) => {
    const next = selectedTypes
      .filter((type) => CONTRACT_FILTER_TYPES.includes(type))
      .slice(0, 1);
    setContractSearch(next);
    if (next.length > 0) {
      setRecruitmentSearch(['filled']);
    }
  };

  const updateRecruitmentSearch = (selectedTypes) => {
    setRecruitmentSearch(
      selectedTypes.filter((type) => RECRUITMENT_FILTER_TYPES.includes(type))
    );
  };

  const updateSupplierSearch = (selectedValues) => {
    setSupplierSearch(Array.isArray(selectedValues) ? selectedValues : []);
  };

  // Update the departments filter state
  const updateDepartment = (updatedDepartment) => {
    const updated = [...departments];
    for (let i = 0; i < updated.length; i++) {
      for (let j = 0; j < updatedDepartment.length; j++) {
        if (updated[i].name === updatedDepartment[j].value) {
          updated[i].checked = updatedDepartment[j].checked;
        }
      }
    }
    setDepartments(updated);
  };

  // Update the mission period filter state
  const updateMissionPeriod = (updatedMissionPeriod) => {
    const updated = [...missionPeriod];
    for (let i = 0; i < updated.length; i++) {
      for (let j = 0; j < updatedMissionPeriod.length; j++) {
        if (updated[i].type === updatedMissionPeriod[j].value) {
          updated[i].checked = updatedMissionPeriod[j].checked;
        }
      }
    }
    setMissionPeriod(updated);
  };

  const updateStatusSearch = (selectedTypes) => {
    setStatusSearch(normalizeStatusSearch(selectedTypes));
  };

  // Opens the temporary worker info slide-over panel
  const openTempWorkerInfo = (id) => {
    setObjID(id);
    toggleSlideOver('get_temp_worker');
  };

  // Handles the selection of missions for bulk actions
  const handleSelectMission = (missionId, checked) => {
    setSelectedMissions((prev) => {
      if (checked) return [...prev, missionId];
      return prev.filter((id) => id !== missionId);
    });
  };

  // Fetch employees after filters are initialized
  useEffect(() => {
    if (!filtersReady) return;

    ApiDatabase.getEmployeesToAssignMission(
      { token },
      (data) => {
        if (data.employees.length > 0) {
          const source = filterSourceRef.current;
          const urlEmployeeIds = initialUrlParams.current.adminid
            ? initialUrlParams.current.adminid.split(',')
            : [];
          const checkedIds =
            source === 'url'
              ? urlEmployeeIds
              : source === 'localStorage'
                ? savedEmployeesRef.current
                : [];
          const updated = data.employees
            .map((employee) => ({
              ...employee,
              checked: checkedIds.includes(employee._id),
            }))
            .sort((a, b) => a.firstname.localeCompare(b.firstname));
          updated.push({
            firstname: 'Non assigné',
            lastname: '',
            checked: checkedIds.includes('unassign'),
            _id: 'unassign',
          });
          setEmployees(updated);
          setEmployeesSearch(updated);
        }
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [filtersReady, token]);

  // Update employee search filter state, handling special case for unassigned
  const updateEmployeeSearch = (updatedEmployeeSearch) => {
    // Clone employeesSearch array to avoid direct state mutation
    const updated = employeesSearch.map((emp) => ({ ...emp }));

    // Create a mapping object for quick access to updatedEmployeeSearch items
    const searchMap = updatedEmployeeSearch.reduce((acc, item) => {
      acc[item.value] = item;
      return acc;
    }, {});

    // Check if 'unassign' is checked in updatedEmployeeSearch
    const isUnassignInUpdatedEmployeeSearchChecked = !!(
      searchMap['unassign'] && searchMap['unassign'].checked
    );
    // Find the 'unassign' item in the cloned updated array
    const unassignItem = updated.find((item) => item._id === 'unassign');
    // Check if 'unassign' is checked in updated array
    const isUnassignInUpdatedChecked = unassignItem && unassignItem.checked;

    if (
      isUnassignInUpdatedEmployeeSearchChecked &&
      !isUnassignInUpdatedChecked
    ) {
      // If 'unassign' is checked in updatedEmployeeSearch but not in updated:
      // Check 'unassign' and uncheck all others.
      updated.forEach((item) => {
        item.checked = item._id === 'unassign';
      });
    } else if (!isUnassignInUpdatedEmployeeSearchChecked) {
      // If 'unassign' is not checked in updatedEmployeeSearch:
      // Update each item based on the mapping.
      updated.forEach((item) => {
        if (searchMap[item._id] !== undefined) {
          item.checked = searchMap[item._id].checked;
        }
      });
    } else if (
      isUnassignInUpdatedEmployeeSearchChecked &&
      isUnassignInUpdatedChecked
    ) {
      // If 'unassign' is checked in both arrays:
      // Uncheck 'unassign' in updated and update the other items.
      updated.forEach((item) => {
        if (item._id === 'unassign') {
          item.checked = false;
        } else if (searchMap[item._id] !== undefined) {
          item.checked = searchMap[item._id].checked;
        }
      });
    }

    setEmployeesSearch(updated);
  };

  // Assigns an employee to multiple selected missions
  const updateEmployee = (updatedEmployee) => {
    const selectedEmployee = updatedEmployee.find(
      (employee) => employee.checked
    );
    const newEmployeeId = selectedEmployee.value;
    const adminEmployeeAssigned =
      newEmployeeId === 'unassign' ? null : newEmployeeId;

    ApiDatabase.postMissionAdminEmployeeAssigned(
      {
        token,
        idEmployee: newEmployeeId,
        idMissions: selectedMissions,
      },
      (data) => {
        selectedMissions.forEach((missionId) => {
          ApiDatabase.patchSearchMissionCache(missionId, (mission) => ({
            ...mission,
            admin_employee_assigned: adminEmployeeAssigned,
          }));
        });
        setSearchMissionResult((prevResults) =>
          prevResults.map((mission) =>
            selectedMissions.includes(mission.id_mission_real)
              ? {
                  ...mission,
                  admin_employee_assigned: adminEmployeeAssigned,
                }
              : mission
          )
        );
        setSelectedMissions([]);
        toast.success('Assignation effectuée');
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  };

  // Assigns an employee to a single mission and updates the result list
  const updateEmployeeForMission = (missionId, updatedEmployee) => {
    const selectedEmployee = updatedEmployee.find(
      (employee) => employee.checked
    );
    const newEmployeeId = selectedEmployee.value;
    const adminEmployeeAssigned =
      newEmployeeId === 'unassign' ? null : newEmployeeId;

    ApiDatabase.postMissionAdminEmployeeAssigned(
      {
        token,
        idEmployee: newEmployeeId,
        idMissions: [missionId],
      },
      (data) => {
        ApiDatabase.patchSearchMissionCache(missionId, (mission) => ({
          ...mission,
          admin_employee_assigned: adminEmployeeAssigned,
        }));
        setSearchMissionResult((prevMissions) =>
          prevMissions.map((mission) =>
            mission.id_mission_real === missionId
              ? {
                  ...mission,
                  admin_employee_assigned: adminEmployeeAssigned,
                }
              : mission
          )
        );

        toast.success('Assignation effectuée');
      },
      (err) => console.error(err)
    );
  };

  const resolveFilterState = useCallback(
    (overrides = {}) => ({
      companySearch:
        'companySearch' in overrides ? overrides.companySearch : companySearch,
      assignedTempWorkerSearch:
        'assignedTempWorkerSearch' in overrides
          ? overrides.assignedTempWorkerSearch
          : assignedTempWorkerSearch,
      recruitmentSearch:
        'recruitmentSearch' in overrides ? overrides.recruitmentSearch : recruitmentSearch,
      supplierSearch:
        'supplierSearch' in overrides ? overrides.supplierSearch : supplierSearch,
      contractSearch:
        'contractSearch' in overrides ? overrides.contractSearch : contractSearch,
      departments: overrides.departments ?? departments,
      missionPeriod: overrides.missionPeriod ?? missionPeriod,
      statusSearch: overrides.statusSearch ?? statusSearch,
      employeesSearch: overrides.employeesSearch ?? employeesSearch,
    }),
    [
      companySearch,
      assignedTempWorkerSearch,
      recruitmentSearch,
      supplierSearch,
      contractSearch,
      departments,
      missionPeriod,
      statusSearch,
      employeesSearch,
    ]
  );

  const getFiltersSnapshot = useCallback(
    (overrides = {}) =>
      JSON.stringify(serializeFilters(resolveFilterState(overrides))),
    [resolveFilterState]
  );

  const getHasFilterChanges = useCallback(
    (overrides = {}) => getFiltersSnapshot(overrides) !== savedFiltersSnapshot,
    [getFiltersSnapshot, savedFiltersSnapshot]
  );

  const hasFilterChanges = getHasFilterChanges();

  const resetFiltersToDefault = useCallback(() => {
    setSearch('');
    setCompanySearch([]);
    setAssignedTempWorkerSearch([]);
    setRecruitmentSearch([]);
    setSupplierSearch([]);
    setContractSearch([]);
    setDepartments((prev) => prev.map((d) => ({ ...d, checked: false })));
    setMissionPeriod((prev) =>
      prev.map((p) => ({
        ...p,
        checked: false,
      }))
    );
    setStatusSearch([]);
    setEmployeesSearch((prev) => prev.map((e) => ({ ...e, checked: false })));
    setMissionDateRange([null, null]);
    setCurrentPage(1);
    setHiddenByFiltersCount(0);
  }, []);

  const saveDefaultFilters = useCallback(
    (overrides = {}) => {
      if (!userData?._id) return;
      const filterState = resolveFilterState(overrides);
      const serialized = serializeFilters(filterState);
      localStorage.setItem(getFilterStorageKey(userData._id), JSON.stringify(serialized));
      setSavedFiltersSnapshot(toSavedSnapshot(serialized));
      setHasSavedFilters(true);
      savedDepartmentsRef.current = serialized.departments;
      savedEmployeesRef.current = serialized.employeesSearch;

      if ('companySearch' in overrides) {
        setCompanySearch(normalizeSelectedSearchOptions(overrides.companySearch));
      }
      if ('assignedTempWorkerSearch' in overrides) {
        setAssignedTempWorkerSearch(
          normalizeSelectedSearchOptions(overrides.assignedTempWorkerSearch)
        );
      }
      if ('recruitmentSearch' in overrides) {
        setRecruitmentSearch(overrides.recruitmentSearch);
      }
      if ('supplierSearch' in overrides) {
        setSupplierSearch(overrides.supplierSearch);
      }
      if ('contractSearch' in overrides) {
        setContractSearch(overrides.contractSearch);
      }
      if ('statusSearch' in overrides) {
        setStatusSearch(overrides.statusSearch);
      }

      toast.success('Filtres sauvegardés');
    },
    [userData, resolveFilterState]
  );

  // Return all states and functions needed by the MissionPeriod component
  return {
    search,
    setSearch,
    missionDateRange,
    setMissionDateRange,
    missionDateStart,
    missionDateEnd,
    searchMissionResult,
    searchMissionResultWaiting,
    companySearch,
    setCompanySearch,
    assignedTempWorkerSearch,
    setAssignedTempWorkerSearch,
    recruitmentSearch,
    updateRecruitmentSearch,
    supplierSearch,
    updateSupplierSearch,
    contractSearch,
    updateContractSearch,
    departments,
    updateDepartment,
    missionPeriod,
    updateMissionPeriod,
    statusSearch,
    updateStatusSearch,
    employees,
    employeesSearch,
    updateEmployee,
    updateEmployeeSearch,
    openCompanyInfo,
    openTempWorkerInfo,
    handleSelectMission,
    selectedMissions,
    updateEmployeeForMission,
    currentPage,
    setCurrentPage,
    totalPages,
    countPerPage,
    countTotal,
    editMission,
    setEditMission,
    setSearchMissionResult,
    hasFilterChanges,
    getHasFilterChanges,
    saveDefaultFilters,
    hiddenByFiltersCount,
    resetFiltersToDefault,
  };
}
